import React, { useState, useContext, useEffect } from 'react';
import { FieldData, ReleaseItemsRowsFieldType } from 'types/FieldTypes';
import { ReleaseItemType, createNewReleaseItem } from '../../../views/releases/ReleaseTypes';
import { useTranslation } from 'react-i18next';
import { useGlobalContext } from 'GlobalContext';
import { v4 as uuidv4 } from 'uuid';
import { baseUrl } from 'App';
import FormFieldContext from '../FormFieldContext';
import TextareaInput from '../basefields/TextareaInput';
import Dropzone from '../basefields/Dropzone';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash } from '@fortawesome/free-solid-svg-icons';
import '../../../style/scss/live-edit.scss';
import '../../../style/scss/forms.scss';

const ReleaseItemsRowsField: React.FC<ReleaseItemsRowsFieldType & { data: FieldData, viewKey: string }> = ({
    name, data
}) => {
    const { t } = useTranslation();
    const { errorMessages } = useGlobalContext();
    const { editing, setUpdatedData, showErrorAlert } = useContext(FormFieldContext);
    const [rows, setRows] = useState<ReleaseItemType[]>([]);

    // If there are existing release items, set the current rows
    useEffect(() => {
        if (data && data[name] && data[name].length > 0) {

            // Map the paths of the release items images
            const mappedItems = data[name].map((item: ReleaseItemType) => {
                let imagePath = null;

                // Set the right image path
                if (item.image) {
                    imagePath = process.env.NODE_ENV === 'production' ? item.image : `${baseUrl}${item.image}`;
                }

                // Destruct image from the object
                const { image, ...rest } = item;

                return {
                    ...rest,
                    imageExample: imagePath
                };
            });

            // Set the rows
            setRows(mappedItems);

        // If there are no existing release items, create an empty row
        } else {
            const emptyRow = createNewReleaseItem(data['release'], uuidv4());
            setRows([emptyRow]);
        }
    }, []);

    // Handle the input change
    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>, identifier: string) => {
        // Copy the rows and find the row index
        const updatedRows = [...rows];
        const rowIndex = updatedRows.findIndex(row => row.id?.toString() === identifier || row.uuid === identifier);

        if (rowIndex !== -1) {
            // Set the value in the updated data
            updatedRows[rowIndex].title = event.target.value;
            
            // Update the rows and updated data
            setRows(updatedRows);
            setUpdatedData((prev: any) => ({ ...prev, [name]: updatedRows }))
        }        
    };

    // Handle text area change
    const handleTextAreaChange = (value: string | React.ChangeEvent<HTMLTextAreaElement>, identifier: string) => {
        // Get the actual value
        const actualValue = typeof value === 'string' ? value : value.target.value;

        // Copy the rows and find the row index
        const updatedRows = [...rows];
        const rowIndex = updatedRows.findIndex(item => item.id?.toString() === identifier || item.uuid === identifier);

        if (rowIndex !== -1) {
            // Set the description in the updated rows
            updatedRows[rowIndex].description = actualValue;

            // Update rows and updated data
            setRows(updatedRows);
            setUpdatedData((prev: any) => ({ ...prev, [name]: updatedRows }))
        }
    };

    // Handle deletion of a row
    const handleDeleteRow = (identifier: string) => {
        let updatedRows = [...rows];
        const rowIndex = updatedRows.findIndex(row => row.id?.toString() === identifier || row.uuid === identifier);

        if (rowIndex !== -1) {
            // Get the row from the updated rows
            const row = updatedRows[rowIndex]

            // Check if the row is a new row added in the frontend (it has an uuid but no id)
            if (!row.id && row.uuid) {
                // Directly remove it from the updated rows
                updatedRows = updatedRows.filter((_, index) => index !== rowIndex)
        
            } else {
                // Flag the backend known row as deleted without actually delete it
                updatedRows[rowIndex].deleted = true;
            }
    
            setRows(updatedRows);
            setUpdatedData((currentUpdatedData: any) => ({ ...currentUpdatedData, [name]: updatedRows }))
        } 
    };

    // Handle the adding of a new row
    const handleAddRow = () => {
        // Create a new row and add it to the existing rows
        const newRow = createNewReleaseItem(data['release'], uuidv4());
        const updatedRows = [...rows, newRow];
    
        // Update the rows and updated data
        setRows(updatedRows);
        setUpdatedData((currentUpdatedData: any) => ({ ...currentUpdatedData, [name]: updatedRows }))
    };

    // Handle adding of an image
    const handleAddImage = (file: File | null, identifier: string) => {
        if (!file || !file.type.startsWith('image/')) return;
    
        const reader = new FileReader();
        reader.onload = (e: ProgressEvent<FileReader>) => {
            const result = e.target?.result as string;
            const updatedRows = [...rows];
            const rowIndex = updatedRows.findIndex(item => item.id?.toString() === identifier || item.uuid === identifier);
    
            if (rowIndex !== -1) {
                const extension = file.name.substring(file.name.lastIndexOf('.'));
                const randomCode = Math.random().toString(36).substring(2, 7);
                const generatedFilename = `release_item_${randomCode}${extension}`;
    
                updatedRows[rowIndex].image_file = result;
                updatedRows[rowIndex].image_filename = generatedFilename;
                updatedRows[rowIndex].imageExample = result;
                updatedRows[rowIndex].image_deleted = false;
    
                setRows(updatedRows);
                setUpdatedData((prev: any) => ({ ...prev, [name]: updatedRows }));
            }
        };
    
        reader.readAsDataURL(file);
    };

    // Handle the removing of an image
    const handleRemoveImage = (identifier: string) => {
        const updatedRows = [...rows];
        const rowIndex = updatedRows.findIndex(row => row.id?.toString() === identifier || row.uuid === identifier);
    
        if (rowIndex !== -1) {
            updatedRows[rowIndex].image_deleted = true;
            updatedRows[rowIndex].imageExample = null;
            updatedRows[rowIndex].image_file = null;
            updatedRows[rowIndex].image_filename = null;
    
            setRows(updatedRows);
            setUpdatedData((prev: any) => ({ ...prev, [name]: updatedRows }));
        }
    };

    return (
        <div className='rows-field'>
            {showErrorAlert &&
                <div className="alert form-alert alert-danger" role="alert">
                    {t(errorMessages.general, { defaultValue: errorMessages.general })}
                </div>
            }
            {editing && (
                // Edit mode
                <div className='edit-mode'>
                   <div>
                        {rows && rows.filter(row => !row.deleted).map((row, index, filteredRows) => {
                            const identifier = row.id?.toString() || row.uuid || '';
                            return (
                                <div className='list-item'>
                                    <div className={`release-items-row ${index === filteredRows.length - 1 ? 'last-child' : ''}`}>
                                        <div className='release-item-row'>
                                            <input 
                                                type="text"
                                                id={`title_${identifier}`}
                                                name={`title_${identifier}`}
                                                value={row.title ?? ''}
                                                onChange={event => handleInputChange(event, identifier)}
                                                placeholder={t('release.general.title_placeholder')}
                                                autoFocus={false}
                                                className={identifier && errorMessages?.release_items?.[identifier]?.['title'] ? 'is-invalid' : undefined}
                                            />
                                            {row.imageExample ? (
                                                // Show current image
                                                <div className='current-image'>
                                                    <img src={row.imageExample} alt="preview" />
                                                    <div className='details'>
                                                        <div className='remove-button'
                                                                onClick={() => handleRemoveImage(identifier)}>
                                                            {t('forms.delete_image_label')}
                                                        </div>
                                                    </div>
                                                </div>
                                            ) : (
                                                // Show dropzone if there is no image
                                                <Dropzone
                                                    acceptedFileTypes={['.png', '.jpeg', '.jpg', '.heic', '.heif']}
                                                    placeholder={'attachment.general.drag_drop_placeholder'}
                                                    onFileSelect={(file) => handleAddImage(file, identifier)}
                                                />
                                            )}
                                            <TextareaInput
                                                name={`description_${identifier}`}
                                                identifier={`description_${identifier}`}
                                                value={row.description ?? ''}
                                                placeholder='release.general.description_placeholder'
                                                rows={4}
                                                onChange={event => handleTextAreaChange(event, identifier)}
                                            />
                                        </div>
                                        <div className='delete-icon tooltip-icon'>
                                            <FontAwesomeIcon 
                                                icon={faTrash} 
                                                onClick={() => handleDeleteRow(row.id?.toString() || row.uuid || '')} />
                                            <span className="tooltip">{t('general.delete')}</span>
                                        </div>
                                    </div>
                                </div>     
                            )
                        })}
                    </div>
                    <div onClick={(e) => {e.preventDefault(); handleAddRow(); }} 
                        className="add-new-button">
                        {t('forms.add_new_item')}
                    </div>
                </div>
            )}
        </div>
    );
}

export default ReleaseItemsRowsField;